import { findIndex, map } from 'lodash';
import { aclTypes } from 'new/components/collaborate/ResourceDetail/ResourceDetail';
import { getCurrentOrg } from 'js/reducers/selectors';
import {
  ACL_RESOURCE_TYPE_DATASET,
  ACL_RESOURCE_TYPE_DF_DATASET,
  ACL_RESOURCE_TYPE_DF_SINK,
  ACL_RESOURCE_TYPE_DF_SOURCE,
  ACL_RESOURCE_TYPE_ENDPOINTS,
  ACL_RESOURCE_TYPE_FLOW,
} from '../../constants/aclConstants';
import * as actionTypes from '../../constants/actionTypes';
import { GET, POST } from '../../constants/requestMethods';
import { getEffectiveRole } from '../../lib/ACLUtils';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { isFlowNodeResource } from '../../lib/ResourceUtils';

const getACLEndpoint = (aclResourceType, data, role = null) => {
  const effectiveResourceType =
    role === 'sharer' && aclResourceType === ACL_RESOURCE_TYPE_DF_DATASET
      ? ACL_RESOURCE_TYPE_DATASET
      : aclResourceType;

  switch (effectiveResourceType) {
    case ACL_RESOURCE_TYPE_FLOW:
      return `/flows/${data.origin_node_id}/accessors`;

    case ACL_RESOURCE_TYPE_DF_SOURCE:
    case ACL_RESOURCE_TYPE_DF_DATASET:
    case ACL_RESOURCE_TYPE_DF_SINK:
      const isFlowNode = isFlowNodeResource(aclResourceType, data);
      return `/flows/${data.flow_node_id ?? (isFlowNode ? data?.id : undefined)}/accessors`;
    default:
      return `/${ACL_RESOURCE_TYPE_ENDPOINTS[effectiveResourceType]}/${data.id}/accessors`;
  }
};

export const fetchAcl = (aclResourceType, data) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_ACL_REQUEST,
        actionTypes.GET_ACL_SUCCESS,
        actionTypes.GET_ACL_FAILURE,
      ],
      endpoint: getACLEndpoint(aclResourceType, data),
      schema: null,
      requestMethod: GET,
      //Always fetch audit log for a resource in collab mode
      requestInfo: {
        aclResourceType,
        id: data.id,
      },
    },
  });
};

export const fetchAccessInsights =
  ({ resourceType, id, userId }) =>
  (dispatch) => {
    const aclEndpoint = ACL_RESOURCE_TYPE_ENDPOINTS[aclTypes[resourceType]];
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_ACCESS_INSIGHTS_REQUEST,
          actionTypes.GET_ACCESS_INSIGHTS_SUCCESS,
          actionTypes.GET_ACCESS_INSIGHTS_FAILURE,
        ],
        endpoint: `/${aclEndpoint}/${id}/access?user_id=${userId}`,
        schema: null,
        requestMethod: GET,
        //Always fetch audit log for a resource in collab mode
        requestInfo: {
          resourceType,
          userId,
          id,
        },
      },
    });
  };

export const updateAcl =
  (
    aclResourceType,
    data,
    subjectType,
    subjectId,
    role,
    // For sharer delete action, we're storing data_set in byKey, but on delete we're accessing data using df_data_set
    isDeleted = false,
  ) =>
  (dispatch, getState) => {
    const { id } = data;
    const effectiveResourceType =
      role === 'sharer' && aclResourceType === ACL_RESOURCE_TYPE_DF_DATASET
        ? ACL_RESOURCE_TYPE_DATASET
        : aclResourceType;
    const state = getState();
    const currentOrg = getCurrentOrg(state);
    const oldAccessors = state.acl.byKey[effectiveResourceType + id];

    //TODO: NEX-11848: With Nexset share we need to include recipient's org id even if it is in the same org. Need this safeguard at least till API is updated to automatically understand org id.
    const newAccessors =
      role === 'sharer' && aclResourceType === ACL_RESOURCE_TYPE_DF_DATASET
        ? map(oldAccessors, (accessor) => ({
            type: accessor.type,
            id: accessor.id,
            org_id: accessor?.accessor_org_id || '*',
            access_role: accessor.access_role || getEffectiveRole(accessor),
          }))
        : map(oldAccessors, (accessor) => ({
            type: accessor.type,
            id: accessor.id,
            access_role: accessor.access_role || getEffectiveRole(accessor),
          }));
    const updatedAccessorIndex = findIndex(newAccessors, {
      type: subjectType,
      id: subjectId,
    });

    // if no role specified, revoke access
    // Need to redo this for delete for sharer role

    //TODO: NEX-11848: With Nexset share we need to include recipient's org id even if it is in the same org. Need this safeguard at least till API is updated to automatically understand org id.
    if (role) {
      if (updatedAccessorIndex > -1) {
        newAccessors[updatedAccessorIndex].access_role = role;
      } else {
        role === 'sharer' &&
        aclResourceType === ACL_RESOURCE_TYPE_DF_DATASET &&
        subjectType === 'USER'
          ? newAccessors.push({
              type: subjectType,
              id: subjectId,
              org_id: currentOrg.id,
              access_role: role,
            })
          : newAccessors.push({
              type: subjectType,
              id: subjectId,
              access_role: role,
            });
      }
    } else if (updatedAccessorIndex > -1) {
      newAccessors.splice(updatedAccessorIndex, 1);
    }

    // For sharer, delete action
    if (role && isDeleted && updatedAccessorIndex > -1) {
      newAccessors.splice(updatedAccessorIndex, 1);
    }

    const payload = { accessors: newAccessors };

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_ACL_REQUEST,
          actionTypes.UPDATE_ACL_SUCCESS,
          actionTypes.UPDATE_ACL_FAILURE,
        ],
        endpoint: getACLEndpoint(aclResourceType, data, role),
        payload,
        schema: null,
        requestMethod: POST,
        requestInfo: {
          aclResourceType: effectiveResourceType,
          id,
          subjectType,
          subjectId,
        },
      },
    });
  };

export const updateMultipleAcl =
  (aclResourceType, data, accessors, role) => (dispatch, getState) => {
    const { id } = data;
    const effectiveResourceType =
      role === 'sharer' && aclResourceType === ACL_RESOURCE_TYPE_DF_DATASET
        ? ACL_RESOURCE_TYPE_DATASET
        : aclResourceType;
    const oldAccessors = getState().acl.byKey[effectiveResourceType + id];
    let newAccessors = [
      ...map(oldAccessors, (accessor) => ({
        type: accessor.type,
        id: accessor.id,
        access_role: accessor.access_role || getEffectiveRole(accessor),
      })),
    ];

    if (!role) {
      newAccessors = [
        ...newAccessors.filter((accessor) => !accessors.some((a) => a.id === accessor.id)),
      ];
    } else {
      newAccessors = [
        ...newAccessors.filter((accessor) => !accessors.some((a) => a.id === accessor.id)),
        ...map(accessors, (accessor) => ({
          type: accessor.optype,
          id: accessor.id,
          access_role: role,
        })),
      ];
    }

    const payload = { accessors: newAccessors };

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_ACL_REQUEST,
          actionTypes.UPDATE_ACL_SUCCESS,
          actionTypes.UPDATE_ACL_FAILURE,
        ],
        endpoint: getACLEndpoint(aclResourceType, data, role),
        payload,
        schema: null,
        requestMethod: POST,
        requestInfo: {
          aclResourceType: effectiveResourceType,
          id,
          accessors,
        },
      },
    });
  };

export const clearAcl = () => ({
  type: actionTypes.CLEAR_ACL,
});
